import {
  mapV1FieldsToV2Data as shvMapV1FieldsToV2Data,
  buildCustomizePayload as shvBuildCustomizePayload,
} from '../69842241581723c403c697fc/mappers';
import {
  applyGpsKonditionenDefaults,
  applyGpsPreiseDefaults,
  applyGpsStammdatenDefaults,
} from './gpsFundDefaults';
import { dedupeAllocationByLabel, dedupeTop10ByName } from './dedupeGpsAllocations';
import {
  GPS_HISTORY_ISIN,
  GPS_SECTION_HEADER_DEFAULTS,
  PVM_GPS_HISTORY_LEGEND_LABEL,
  TEMPLATE_ID_PVM_GPS,
  isPvmShvBarChartLabel,
  resolvePvmGpsHistoryLegendLabel,
} from './templateConstants';
import {
  GPS_DEFAULT_ASSET_ALLOCATION,
  GPS_DEFAULT_SECTOR_ALLOCATION,
  GPS_DEFAULT_TOP10_STOCKS,
} from './gpsAllocationDefaults';
import { applyPvmLoremPlaceholders } from './pvmPlaceholders';
import { normalizePvmPeriodPerformance } from './pvmPeriodPerformance';
import moment from 'moment';

function makeDynamicAxionNumber(field: string) {
  return {
    isDynamic: true,
    fields: [
      {
        type: 'table',
        table: 'cixon_stammdaten_extension',
        field,
        valueType: 'number',
      },
      { type: 'text', text: ' EUR' },
    ],
  };
}

function hasFinitePreisValue(raw: unknown): boolean {
  if (raw == null || raw === '') return false;
  if (typeof raw === 'object') return false;
  if (typeof raw === 'number') return Number.isFinite(raw);
  const s = String(raw).trim().replace(/\s/g, '').replace(/eur/gi, '');
  if (!s || /^nan$/i.test(s) || /^k\.\s*a\.?$/i.test(s)) return false;
  const n = Number(s.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) || /^[+-]?\d+([.,]\d+)?$/.test(s);
}

/** Ensure NAV / Fondsvermögen resolve via Axion when not stored as scalars. */
function ensurePvmDynamicPreise(preise: Record<string, unknown>): Record<string, unknown> {
  const next = { ...preise };
  if (!hasFinitePreisValue(next.fondsvolumen)) {
    next.fondsvolumen = makeDynamicAxionNumber('fondsvermoegen');
  }
  // Kurs (NAV) is its own field — do not skip when leftover mepreis/ausgabepreis scalars exist
  if (!hasFinitePreisValue(next.ruecknahmepreis)) {
    next.ruecknahmepreis = makeDynamicAxionNumber('ruecknahmepreis');
  }
  return next;
}

function resolvePvmValuationDate(
  preise: Record<string, unknown>,
  additional: Record<string, unknown>,
): Record<string, unknown> {
  const historyUntil = additional?.historyUntil || additional?.valuationDate;
  if (!historyUntil) return preise;

  const next = { ...preise };
  const untilIso = moment(String(historyUntil).slice(0, 10), 'YYYY-MM-DD', true);
  if (!untilIso.isValid()) return next;

  const kursper = next.kursper ? moment(String(next.kursper).slice(0, 10), ['YYYY-MM-DD', 'DD.MM.YYYY'], true) : null;
  const kursperBad =
    !kursper?.isValid() ||
    kursper.isAfter(untilIso, 'day') ||
    (kursper.year() >= 2026 && kursper.month() === 6 && untilIso.month() <= 5);

  if (kursperBad) {
    next.kursper = untilIso.format('YYYY-MM-DD');
  }
  return next;
}

function deepEqual(a: any, b: any) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
}

export function mapV1FieldsToV2Data(fields: any, templateId?: string) {
  const effectiveTemplateId = templateId || TEMPLATE_ID_PVM_GPS;
  const additional = fields?.additional_fields || {};
  const v2 = shvMapV1FieldsToV2Data(fields, effectiveTemplateId);

  v2.assetAllocation = dedupeAllocationByLabel(
    additional?.assetAllocation?.length ? additional.assetAllocation : GPS_DEFAULT_ASSET_ALLOCATION,
  );
  v2.top10Stocks = dedupeTop10ByName(
    additional?.top10Stocks?.length ? additional.top10Stocks : GPS_DEFAULT_TOP10_STOCKS,
  );
  v2.sectorAllocation = dedupeAllocationByLabel(
    additional?.sectorAllocation?.length ? additional.sectorAllocation : GPS_DEFAULT_SECTOR_ALLOCATION,
  );
  v2.page2ImageUrl =
    additional?.page2ImageUrl ||
    additional?.page2LeftImageUrl ||
    v2.page2ImageUrl ||
    null;
  v2.page2ImageUrl2 = additional?.page2ImageUrl2 || v2.page2ImageUrl2 || null;
  v2.page2ImageUrl3 = additional?.page2ImageUrl3 || v2.page2ImageUrl3 || null;
  v2.manImageUrl = additional?.manImageUrl || v2.manImageUrl || null;

  v2.historyIsin = additional?.historyIsin || v2.historyIsin || GPS_HISTORY_ISIN;
  v2.historyLegendLabel = resolvePvmGpsHistoryLegendLabel(
    additional?.historyLegendLabel || v2.historyLegendLabel || PVM_GPS_HISTORY_LEGEND_LABEL,
  );

  // Drop SHV clone leftovers under the years bar chart
  if (isPvmShvBarChartLabel(v2.wertentwicklung_nach_jahren_label)) {
    v2.wertentwicklung_nach_jahren_label = '';
  }

  v2.historyUntil = additional?.historyUntil || v2.historyUntil;

  v2.stammdaten = applyGpsStammdatenDefaults(v2.stammdaten || {});
  v2.preise = ensurePvmDynamicPreise(
    applyGpsPreiseDefaults(resolvePvmValuationDate(v2.preise || {}, additional)),
  );
  v2.konditionen = applyGpsKonditionenDefaults(v2.konditionen || {});

  applyPvmLoremPlaceholders(v2);

  v2.sectionHeaders = {
    ...GPS_SECTION_HEADER_DEFAULTS,
    ...(additional?.sectionHeaders || {}),
    ...(v2.sectionHeaders || {}),
  };

  // WERTENTWICKLUNG NACH PERIODEN: Rendite + 1M / YTD / 1J / 3J / seit Auflage
  v2.wertentwicklung_nach_perioden = normalizePvmPeriodPerformance(
    v2.wertentwicklung_nach_perioden,
  );

  // GPS page 2 uses additional_fields allocations — not SHV pie / TOP-5 fields.
  v2.gesamtportfolio = [];
  v2.branchen = [];
  v2.regionen = [];
  v2.marktkapitalisierung = [];
  v2.pie_addition = null;

  return v2;
}

export function buildCustomizePayload(v2Data: any, original: any) {
  const payload = shvBuildCustomizePayload(v2Data, original, TEMPLATE_ID_PVM_GPS);
  const origAdditional = original?.additional_fields || {};
  const gpsUpdates: Record<string, unknown> = {};

  if (v2Data?.assetAllocation && !deepEqual(origAdditional.assetAllocation, v2Data.assetAllocation)) {
    gpsUpdates.assetAllocation = v2Data.assetAllocation;
  }
  if (v2Data?.top10Stocks && !deepEqual(origAdditional.top10Stocks, v2Data.top10Stocks)) {
    gpsUpdates.top10Stocks = v2Data.top10Stocks;
  }
  if (v2Data?.sectorAllocation && !deepEqual(origAdditional.sectorAllocation, v2Data.sectorAllocation)) {
    gpsUpdates.sectorAllocation = v2Data.sectorAllocation;
  }
  if (v2Data?.page2ImageUrl !== undefined && v2Data.page2ImageUrl !== origAdditional.page2ImageUrl) {
    gpsUpdates.page2ImageUrl = v2Data.page2ImageUrl;
  }
  if (v2Data?.page2ImageUrl2 !== undefined && v2Data.page2ImageUrl2 !== origAdditional.page2ImageUrl2) {
    gpsUpdates.page2ImageUrl2 = v2Data.page2ImageUrl2;
  }
  if (v2Data?.page2ImageUrl3 !== undefined && v2Data.page2ImageUrl3 !== origAdditional.page2ImageUrl3) {
    gpsUpdates.page2ImageUrl3 = v2Data.page2ImageUrl3;
  }
  if (v2Data?.manImageUrl !== undefined && v2Data.manImageUrl !== origAdditional.manImageUrl) {
    gpsUpdates.manImageUrl = v2Data.manImageUrl;
  }

  if (!Object.keys(gpsUpdates).length) {
    return payload;
  }

  const additionalFieldsIndex = payload.findIndex((entry) => entry.key === 'additional_fields');
  if (additionalFieldsIndex >= 0) {
    payload[additionalFieldsIndex] = {
      key: 'additional_fields',
      value: { ...payload[additionalFieldsIndex].value, ...gpsUpdates },
    };
  } else {
    payload.push({
      key: 'additional_fields',
      value: { ...origAdditional, ...gpsUpdates },
    });
  }

  return payload;
}
